// src/client/utils/currency.ts
// Mirrors @admin/utils/money. Duplicated rather than imported so the borrower
// surface has no cross-surface dependency on admin code.
import type { Ngwee } from '@/lib/types';

export function formatKwacha(
  amount: Ngwee,
  opts: { showUnit?: boolean; decimals?: 0 | 2 } = {},
): string {
  const { showUnit = true, decimals = 2 } = opts;
  const kwacha = Number(amount) / 100;
  const formatted = kwacha.toLocaleString('en-ZM', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return showUnit ? `K ${formatted}` : formatted;
}

// Loan principals are non-negative whole-ngwee amounts. Reject anything that
// isn't a clean unsigned decimal with at most 2 fractional digits — that
// covers garbage like "K -100", scientific notation ("1e3"), and sub-ngwee
// precision ("1.005") which would otherwise round silently.
//
// Strip only known display-formatting characters (currency symbol, whitespace,
// thousands separators) — not arbitrary alpha — so that inputs containing
// other letters (e.g. the `e` in "1e3") fail the post-strip regex check
// instead of being silently sanitized into a different valid number.
const KWACHA_DECIMAL_REGEX = /^\d+(\.\d{1,2})?$/;

export function kwachaStringToNgwee(str: string): Ngwee | null {
  const cleaned = str.replace(/[K,\s]/g, '');
  if (!KWACHA_DECIMAL_REGEX.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  const ngwee = Math.round(n * 100);
  if (!Number.isInteger(ngwee)) return null;
  return ngwee as Ngwee;
}

export function ngweeToKwachaNumber(amount: Ngwee): number {
  return Number(amount) / 100;
}
